import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Divider, Stack} from 'views/components';
import {Tab, TabContext, TabList, TabPanel} from 'views/components';
import {translate} from 'views/module';

import ClientHeader from './header/ClientHeader';
import SlashCommands from './slash_commands/SlashCommands';
import Correspondances from './correspondances/Correspondances';
import ClientCalendar from './schedule/Schedule';
import CaregiverConnection from './caregiver_connection/CaregiverConnection';
import RelationshipList from './relationships/RelationshipList';
import Payment from './payment/Payment';
import CareviewSettings from './careview_settings/CareviewSettings';
import Careplan from './careplan/Careplan';
import Career from './career/Career';
import Forms from './forms/Forms';
import PlaybookView from './playbooks/PlaybookView';
import ClientTaskOverview from './tasks/TaskOverview';
import Tasks from './tasks/TaskList';

const ClientFeed = (props) => {
  const {feedTab, setFeedTab} = props;
  const {
    clientTasksFilter,
    setClientTasksFilter,
  } = props.clientTasksFilterOptions;
  const [shouldScrollToTasks, setShouldScrollToTasks] = React.useState(false);
  const tasksSectionRef = React.useRef(null);

  const handleChange = (event, newValue) => {
    setFeedTab(newValue);
  };

  const handleViewAllPlaybookTasks = React.useCallback((playbookRn) => {
    setClientTasksFilter(playbookRn);
    setFeedTab('TASKS');
    setShouldScrollToTasks(true);
  }, [setClientTasksFilter, setFeedTab]);

  React.useEffect(() => {
    if (!shouldScrollToTasks || feedTab !== 'TASKS') {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      tasksSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setShouldScrollToTasks(false);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [feedTab, shouldScrollToTasks]);

  return (
    <Stack spacing={2} divider={<Divider />}>
      <ClientHeader
        {...props}
      />
      <SlashCommands
        {...props}
      />
      <PlaybookView
        {...props}
        onViewAllPlaybookTasks={handleViewAllPlaybookTasks}
      />
      <ClientTaskOverview
        {...props}
      />
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <TabContext
          value={feedTab}
        >
          <TabList
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={translate('client.ClientDetail.Careplan.Title')}
              value="CAREPLAN"
            />
            <Tab
              label={translate('client.ClientDetail.FormSettings.Title')}
              value="FORMS"
            />
            <Tab
              label={translate('client.ClientDetail.Correspondance.Title')}
              value="CORRESPONDANCE"
            />
            <Tab
              label={translate('client.ClientDetail.Tasks.Title')}
              value="TASKS"
            />
            <Tab
              label={translate('client.ClientDetail.Scheduling.Title')}
              value="SCHEDULING"
            />
            <Tab
              label={translate('client.ClientDetail.CaregiverConnections.Title')}
              value="CAREGIVER"
            />
            <Tab
              label={translate('client.ClientDetail.Relationships.Title')}
              value="RELATIONSHIP"
            />
            <Tab
              label={translate('client.ClientDetail.Payment.Title')}
              value="PAYMENT"
            />
            <Tab
              label={translate('client.ClientDetail.CareviewSettings.Title')}
              value="CAREVIEW_SETTINGS"
            />
            <Tab
              label={translate('client.ClientDetail.Career.Title')}
              value="CAREER"
            />
          </TabList>
          <TabPanel
            value={'CORRESPONDANCE'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Correspondances
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'SCHEDULING'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <ClientCalendar
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CAREGIVER'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <CaregiverConnection
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'RELATIONSHIP'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <RelationshipList
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'PAYMENT'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Payment
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CAREVIEW_SETTINGS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <CareviewSettings
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'FORMS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Forms
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CAREPLAN'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Careplan
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CAREER'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Career
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'TASKS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Box ref={tasksSectionRef}>
              {
                props.clientTasksLoading ?
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '160px',
                    }}
                  >
                    <CircularProgress />
                  </Box> :
                  <Tasks
                    {...props}
                    clientTasksFilterOptions={{clientTasksFilter, setClientTasksFilter}}
                  />
              }
            </Box>
          </TabPanel>
        </TabContext>
      </Box>
    </Stack>
  );
};
ClientFeed.propTypes = {
  clientBackend: PropTypes.object.isRequired,
  clientRn: PropTypes.string.isRequired,
  requesterRn: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  clientData: PropTypes.object.isRequired,
  clientTasksLoading: PropTypes.bool.isRequired,
  feedTab: PropTypes.string.isRequired,
  setFeedTab: PropTypes.func.isRequired,
  clientTasksFilterOptions: PropTypes.shape({
    clientTasksFilter: PropTypes.string.isRequired,
    setClientTasksFilter: PropTypes.func.isRequired,
  }).isRequired,
};

export default React.memo(ClientFeed);
